import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, ShieldAlert, ShieldCheck, KeyRound, UserPlus } from 'lucide-react';
import type { BusinessDetailsView } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Modal,
  StatusBadge,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import {
  createPosCredentialCommand,
  createPosUserCommand,
  getBranchPosControl,
  setPosOperationalStatus,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';

type CreateRole = 'admin' | 'pharmacist' | 'cashier';

type PosControlView = {
  caBranchId: string;
  productionBranchId: string;
  operationalStatus?: 'ACTIVE' | 'SUSPENDED';
  statusVersion?: number;
  statusChangedAt?: string | null;
  statusChangedBy?: string | null;
  reason?: string | null;
  lastCheckInAt?: string | null;
  controlSupported?: boolean;
  controlCapable?: boolean;
  installations?: Array<{
    deviceId: string;
    installationRole: string;
    appVersion?: string | null;
    lastCheckInAt?: string | null;
    isCredentialAuthority?: boolean;
    controlProtocolVersion?: number;
  }>;
  users?: Array<{
    localUserId: string;
    username: string;
    fullName?: string | null;
    role?: string | null;
    isActive: boolean;
    lastReportedAt?: string;
  }>;
  recentCommands?: Array<{
    commandId: string;
    commandVersion: number;
    commandType?: string;
    localUserId?: string | null;
    requestedUsername?: string | null;
    fullName?: string | null;
    role?: string | null;
    createdLocalUserId?: string | null;
    status: string;
    issuedAt: string;
    expiresAt: string;
    passwordChanged?: boolean;
    errorCode?: string | null;
  }>;
};

type LocalUser = NonNullable<PosControlView['users']>[number];

const CREATE_USERNAME_RE = /^[a-z0-9_-]{3,32}$/;

export function PosControlManager({
  details,
  mode,
}: {
  details: BusinessDetailsView;
  mode: 'operation' | 'access';
}) {
  const can = useCan();
  const canReadOp = can('pos_operation.read');
  const canManageOp = can('pos_operation.manage');
  const canReadCred = can('pos_credentials.read');
  const canManageCred = can('pos_credentials.manage');

  const linkedBranches = useMemo(
    () =>
      (details.operationalBranches ?? []).filter(
        (b) => Boolean(b.caBranchId) && Boolean(b.productionBranchId),
      ),
    [details.operationalBranches],
  );

  const [branchId, setBranchId] = useState(linkedBranches[0]?.caBranchId ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<PosControlView | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [credUser, setCredUser] = useState<LocalUser | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFullName, setCreateFullName] = useState('');
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createPasswordConfirm, setCreatePasswordConfirm] = useState('');
  const [createRole, setCreateRole] = useState<CreateRole>('cashier');
  const [createFormError, setCreateFormError] = useState<string | null>(null);

  const selectedBranch = linkedBranches.find((b) => b.caBranchId === branchId) ?? null;
  const businessId = details.overview.id;
  const createEnabled = Boolean(view?.controlCapable) && canManageCred;

  const reload = useCallback(async () => {
    if (!branchId) return;
    if (mode === 'operation' && !canReadOp) return;
    if (mode === 'access' && !canReadCred) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBranchPosControl(businessId, branchId);
      setView(data as PosControlView);
    } catch (e) {
      setError(errorMessage(e));
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, businessId, canReadCred, canReadOp, mode]);

  useEffect(() => {
    if (!branchId && linkedBranches[0]?.caBranchId) {
      setBranchId(linkedBranches[0].caBranchId);
      return;
    }
    void reload();
  }, [branchId, linkedBranches, reload]);

  function clearCreatePasswords() {
    setCreatePassword('');
    setCreatePasswordConfirm('');
  }

  function resetCreateForm() {
    setCreateFullName('');
    setCreateUsername('');
    clearCreatePasswords();
    setCreateRole('cashier');
    setCreateFormError(null);
  }

  async function applyStatus(status: 'ACTIVE' | 'SUSPENDED') {
    if (!branchId) return;
    setBusy(true);
    setError(null);
    try {
      await setPosOperationalStatus(businessId, branchId, {
        status,
        reason: reason.trim() || null,
      });
      setSuspendOpen(false);
      setReactivateOpen(false);
      setReason('');
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitCredentials() {
    if (!branchId || !credUser) return;
    setBusy(true);
    setError(null);
    try {
      await createPosCredentialCommand(businessId, branchId, {
        localUserId: credUser.localUserId,
        username: username.trim() || null,
        password: password || null,
        allowInactive: !credUser.isActive,
      });
      setCredUser(null);
      setUsername('');
      setPassword('');
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitCreateUser() {
    if (!branchId || !createEnabled) return;
    setCreateFormError(null);
    const fullName = createFullName.trim();
    const normalizedUsername = createUsername.trim().toLowerCase();
    if (!fullName) {
      setCreateFormError('Full name is required');
      return;
    }
    if (!CREATE_USERNAME_RE.test(normalizedUsername)) {
      setCreateFormError('Username must be 3–32 chars: a-z, 0-9, _ or -');
      return;
    }
    if (createPassword.length < 8) {
      setCreateFormError('Password must be at least 8 characters');
      return;
    }
    if (createPassword.length > 128) {
      setCreateFormError('Password must be at most 128 characters');
      return;
    }
    if (createPassword !== createPasswordConfirm) {
      setCreateFormError('Password confirmation does not match');
      return;
    }
    if (!['admin', 'pharmacist', 'cashier'].includes(createRole)) {
      setCreateFormError('Role is required');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await createPosUserCommand(businessId, branchId, {
        fullName,
        username: normalizedUsername,
        password: createPassword,
        role: createRole,
      });
      clearCreatePasswords();
      setCreateOpen(false);
      resetCreateForm();
      await reload();
    } catch (e) {
      clearCreatePasswords();
      setCreateFormError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (linkedBranches.length === 0) {
    return (
      <EmptyState
        title="No Production-linked branches"
        description="Confirm a BRANCH identity link before managing POS Operation or POS Access."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={mode === 'operation' ? 'POS Operation' : 'POS Access'}
          description={
            mode === 'operation'
              ? 'Central Branch operational permission (ACTIVE / SUSPENDED). Separate from Device License.'
              : 'Remote username/password and create-user commands for POS. Applied by MAIN/SINGLE.'
          }
          actions={
            <Button type="button" variant="secondary" size="sm" onClick={() => void reload()} disabled={loading}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          }
        />
        <CardBody className="space-y-4">
          <FormField label="Branch">
            <select
              className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              {linkedBranches.map((b) => (
                <option key={b.caBranchId!} value={b.caBranchId!}>
                  {b.name}
                </option>
              ))}
            </select>
          </FormField>

          {error && <p className="text-sm text-danger">{error}</p>}
          {loading && <LoadingState label="Loading POS control state…" />}

          {!loading && view && mode === 'operation' && (
            <div className="space-y-3">
              <DescriptionRow label="Operational status">
                <StatusBadge
                  tone={view.operationalStatus === 'SUSPENDED' ? 'danger' : 'success'}
                  label={view.operationalStatus ?? 'ACTIVE'}
                />
              </DescriptionRow>
              <DescriptionRow label="Status version">{String(view.statusVersion ?? '—')}</DescriptionRow>
              <DescriptionRow label="Changed">
                {view.statusChangedAt
                  ? `${formatDateTime(view.statusChangedAt)} (${formatRelative(view.statusChangedAt)})`
                  : '—'}
              </DescriptionRow>
              <DescriptionRow label="Changed by">{view.statusChangedBy || '—'}</DescriptionRow>
              <DescriptionRow label="Reason">{view.reason || '—'}</DescriptionRow>
              <DescriptionRow label="Last POS check-in">
                {view.lastCheckInAt
                  ? `${formatDateTime(view.lastCheckInAt)} (${formatRelative(view.lastCheckInAt)})`
                  : 'Never'}
              </DescriptionRow>
              <DescriptionRow label="Control support">
                {view.controlSupported ? (
                  <Badge tone="success">Supported / connected</Badge>
                ) : (
                  <Badge tone="warning">Never checked in / unsupported</Badge>
                )}
              </DescriptionRow>
              <DescriptionRow label="MAIN/SINGLE authority">
                {view.controlCapable ? 'Established' : 'Not established'}
              </DescriptionRow>
              {(view.installations ?? [])[0]?.appVersion && (
                <DescriptionRow label="POS app version">
                  {(view.installations ?? [])[0]?.appVersion}
                </DescriptionRow>
              )}

              {!view.controlSupported && (
                <p className="text-sm text-ink-secondary">
                  Status may be stored centrally, but enforcement is not confirmed until a control-capable POS
                  checks in.
                </p>
              )}

              {canManageOp && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {view.operationalStatus !== 'SUSPENDED' ? (
                    <Button type="button" variant="danger" onClick={() => setSuspendOpen(true)}>
                      <ShieldAlert className="h-3.5 w-3.5" />
                      Suspend POS
                    </Button>
                  ) : (
                    <Button type="button" variant="primary" onClick={() => setReactivateOpen(true)}>
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Reactivate POS
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {!loading && view && mode === 'access' && (
            <div className="space-y-4">
              {!view.controlCapable && (
                <p className="text-sm text-ink-secondary">
                  Create User and credential actions stay disabled until a control-capable MAIN/SINGLE POS checks
                  in.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                {canManageCred && (
                  <Button
                    type="button"
                    variant="primary"
                    disabled={!createEnabled}
                    onClick={() => {
                      resetCreateForm();
                      setCreateOpen(true);
                    }}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Create POS User
                  </Button>
                )}
              </div>

              <TableContainer>
                <Table>
                  <THead>
                    <TR>
                      <TH>Full name</TH>
                      <TH>Username</TH>
                      <TH>Role</TH>
                      <TH>Active</TH>
                      <TH>Local user ID</TH>
                      <TH>Last reported</TH>
                      <TH>Actions</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {(view.users ?? []).length === 0 ? (
                      <TR>
                        <TD colSpan={7}>No local POS users reported yet.</TD>
                      </TR>
                    ) : (
                      (view.users ?? []).map((u) => {
                        const latest = (view.recentCommands ?? []).find(
                          (c) =>
                            (c.commandType ?? 'SET_CREDENTIALS') === 'SET_CREDENTIALS' &&
                            c.localUserId === u.localUserId,
                        );
                        return (
                          <TR key={u.localUserId}>
                            <TD>{u.fullName || '—'}</TD>
                            <TD>{u.username}</TD>
                            <TD>{u.role || '—'}</TD>
                            <TD>{u.isActive ? 'Yes' : 'No'}</TD>
                            <TD className="font-mono text-xs">{u.localUserId}</TD>
                            <TD>{u.lastReportedAt ? formatRelative(u.lastReportedAt) : '—'}</TD>
                            <TD>
                              <div className="space-y-1">
                                {latest && (
                                  <div className="text-xs text-ink-secondary">
                                    Cmd v{latest.commandVersion}: {latest.status}
                                  </div>
                                )}
                                {canManageCred && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="secondary"
                                    disabled={!view.controlCapable}
                                    onClick={() => {
                                      setCredUser(u);
                                      setUsername(u.username);
                                      setPassword('');
                                    }}
                                  >
                                    <KeyRound className="h-3.5 w-3.5" />
                                    Change credentials
                                  </Button>
                                )}
                              </div>
                            </TD>
                          </TR>
                        );
                      })
                    )}
                  </TBody>
                </Table>
              </TableContainer>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Recent commands</h3>
                <TableContainer>
                  <Table>
                    <THead>
                      <TR>
                        <TH>Type</TH>
                        <TH>Details</TH>
                        <TH>Status</TH>
                        <TH>Version</TH>
                        <TH>Issued</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {(view.recentCommands ?? []).length === 0 ? (
                        <TR>
                          <TD colSpan={5}>No recent commands.</TD>
                        </TR>
                      ) : (
                        (view.recentCommands ?? []).map((c) => {
                          const type = c.commandType ?? 'SET_CREDENTIALS';
                          const details =
                            type === 'CREATE_USER'
                              ? [
                                  c.requestedUsername || '—',
                                  c.fullName || null,
                                  c.role || null,
                                  c.createdLocalUserId ? `createdLocalUserId ${c.createdLocalUserId}` : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')
                              : [
                                  c.localUserId ? `localUserId ${c.localUserId}` : null,
                                  c.requestedUsername || null,
                                  c.passwordChanged ? 'password changed' : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ');
                          return (
                            <TR key={c.commandId}>
                              <TD>{type === 'CREATE_USER' ? 'Create User' : 'Set Credentials'}</TD>
                              <TD className="text-xs">{details || '—'}</TD>
                              <TD>
                                {c.status}
                                {c.errorCode ? ` (${c.errorCode})` : ''}
                              </TD>
                              <TD className="font-mono text-xs">{c.commandVersion}</TD>
                              <TD>{c.issuedAt ? formatRelative(c.issuedAt) : '—'}</TD>
                            </TR>
                          );
                        })
                      )}
                    </TBody>
                  </Table>
                </TableContainer>
              </div>

              <p className="text-xs text-ink-secondary">
                Created users appear in the Users table only after POS reports them via users/report. Do not treat
                a queued command as applied until status is APPLIED.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        title="Suspend POS operation"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setSuspendOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void applyStatus('SUSPENDED')}>
              Confirm suspend
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Suspend POS operation for:
          <br />
          <strong>
            {details.overview.name} — {selectedBranch?.name}
          </strong>
        </p>
        <p className="mt-2 text-sm text-ink-secondary">
          Supported POS installations at this branch will block operation after receiving the central status.
          This is not a Device License revoke.
        </p>
        <div className="mt-3">
          <FormField label="Reason (optional)">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
        title="Reactivate POS operation"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setReactivateOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="primary" disabled={busy} onClick={() => void applyStatus('ACTIVE')}>
              Confirm reactivate
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Reactivate POS operation for:
          <br />
          <strong>
            {details.overview.name} — {selectedBranch?.name}
          </strong>
        </p>
      </Modal>

      <Modal
        open={Boolean(credUser)}
        onClose={() => setCredUser(null)}
        title="Change POS credentials"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setCredUser(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={busy || (!username.trim() && !password)}
              onClick={() => void submitCredentials()}
            >
              Queue command
            </Button>
          </>
        }
      >
        {credUser && (
          <div className="space-y-3">
            <p className="text-sm">
              Target local user ID <span className="font-mono">{credUser.localUserId}</span>
              {!credUser.isActive && ' (inactive — intentional override)'}
            </p>
            <FormField label="New username (optional)">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} />
            </FormField>
            <FormField label="New password (optional)">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormField>
            <p className="text-xs text-ink-secondary">
              Provide at least one field. Existing password is never shown.
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={createOpen}
        onClose={() => {
          clearCreatePasswords();
          setCreateOpen(false);
        }}
        title="Create POS User"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                clearCreatePasswords();
                setCreateOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" disabled={busy || !createEnabled} onClick={() => void submitCreateUser()}>
              Queue create command
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-secondary">
            Queues a CREATE_USER command for MAIN/SINGLE. The user appears in the table only after POS
            users/report.
          </p>
          <FormField label="Full name">
            <Input
              value={createFullName}
              onChange={(e) => setCreateFullName(e.target.value)}
              maxLength={120}
              autoComplete="off"
            />
          </FormField>
          <FormField label="Username">
            <Input
              value={createUsername}
              onChange={(e) => setCreateUsername(e.target.value)}
              autoComplete="off"
            />
          </FormField>
          <FormField label="Password">
            <Input
              type="password"
              value={createPassword}
              onChange={(e) => setCreatePassword(e.target.value)}
              autoComplete="new-password"
            />
          </FormField>
          <FormField label="Confirm password">
            <Input
              type="password"
              value={createPasswordConfirm}
              onChange={(e) => setCreatePasswordConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </FormField>
          <FormField label="Role">
            <select
              className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value as CreateRole)}
            >
              <option value="admin">Admin</option>
              <option value="pharmacist">Pharmacist</option>
              <option value="cashier">Cashier</option>
            </select>
          </FormField>
          {createFormError && <p className="text-sm text-danger">{createFormError}</p>}
        </div>
      </Modal>
    </div>
  );
}

function DescriptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 text-sm">
      <div className="text-ink-secondary">{label}</div>
      <div>{children}</div>
    </div>
  );
}
